import React, { useState, useEffect, useCallback } from 'react';

/**
 * TimecodeInput - A component for editing SRT-format timecodes
 * 
 * Props:
 * - value: number (seconds with milliseconds as decimal)
 * - onChange: function(seconds: number) - called when value changes
 * - onBlur: function - called when input loses focus
 * - disabled: boolean
 * - style: object - additional styles
 * - className: string - additional CSS classes
 */
const TimecodeInput = ({ 
  value, 
  onChange, 
  onBlur, 
  disabled = false, 
  style = {}, 
  className = '',
  placeholder = '00:00:00,000'
}) => {
  // Convert seconds to SRT format (HH:MM:SS,mmm)
  const secondsToTimecode = (seconds) => {
    if (seconds == null || isNaN(seconds)) return '00:00:00,000';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 1000);
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')},${String(ms).padStart(3, '0')}`;
  };

  // Convert SRT format to seconds
  const timecodeToSeconds = (timecode) => {
    if (!timecode) return 0;
    // Support both comma and period as millisecond separator
    const match = timecode.match(/(\d{1,2}):(\d{1,2}):(\d{1,2})[,.](\d{1,3})/);
    if (!match) return 0;
    const [, hrs, mins, secs, ms] = match;
    return parseInt(hrs) * 3600 + parseInt(mins) * 60 + parseInt(secs) + parseInt(ms.padEnd(3, '0')) / 1000;
  };

  const [displayValue, setDisplayValue] = useState(secondsToTimecode(value));
  const [isEditing, setIsEditing] = useState(false);
  const [isValid, setIsValid] = useState(true);

  // Update display value when prop changes (unless we're currently editing)
  useEffect(() => {
    if (!isEditing) {
      setDisplayValue(secondsToTimecode(value));
    }
  }, [value, isEditing]);

  const validateTimecode = (input) => {
    // Allow partial input during editing
    const partialPattern = /^(\d{0,2}:?){0,3}[,.]?\d{0,3}$/;
    const fullPattern = /^\d{1,2}:\d{1,2}:\d{1,2}[,.]\d{1,3}$/;
    return partialPattern.test(input) || fullPattern.test(input);
  };

  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    
    // Allow the change if it looks like valid partial input
    if (validateTimecode(newValue) || newValue === '') {
      setDisplayValue(newValue);
      
      // Check if it's a complete valid timecode
      const fullPattern = /^\d{1,2}:\d{1,2}:\d{1,2}[,.]\d{1,3}$/;
      setIsValid(fullPattern.test(newValue) || newValue === '');
    }
  }, []);

  const handleFocus = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    
    // Parse and normalize the timecode
    const seconds = timecodeToSeconds(displayValue);
    const normalizedTimecode = secondsToTimecode(seconds);
    
    setDisplayValue(normalizedTimecode);
    setIsValid(true);
    
    // Only call onChange if the value actually changed
    if (seconds !== value) {
      onChange && onChange(seconds);
    }
    
    onBlur && onBlur();
  }, [displayValue, value, onChange, onBlur]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }, []);

  return (
    <input
      type="text"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      placeholder={placeholder}
      className={`form-control form-control-sm timecode-input ${className} ${!isValid ? 'is-invalid' : ''}`}
      style={{
        fontFamily: 'monospace',
        fontSize: '12px',
        width: '110px',
        textAlign: 'center',
        ...style
      }}
    />
  );
};

export default TimecodeInput;
